import React, { useEffect } from 'react';

import { useIntl } from 'react-intl-next';
import invariant from 'tiny-invariant';

import {
	PanelSplitter,
	SideNav,
	SideNavContent,
	SideNavFooter,
	SideNavHeader,
	TopLevelSpacer,
} from '@atlaskit/navigation-system';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

import { AsyncIntlProvider } from '../../common/utils/locale';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import useScrollDirection from '../../common/utils/use-scroll-direction';
import { useOptions } from '../../controllers/options-context';
import { useUserSideNavData } from '../../controllers/user-state/user-side-nav-data-provider';
import {
	SideNavVisibilityProvider,
	useSideNavVisibility,
} from '../../controllers/visibility/use-nav-visibility';

import { messages } from './messages';
import type { SideNavigationLayoutProps } from './types';

/**
 * SideNavigationLayout - A layout component that provides a side navigation structure.
 *
 * This component is designed to be used as a wrapper for the side navigation system,
 * providing a consistent layout with header, content, and footer sections.
 *
 * It utilizes the SideNavVisibilityProvider to manage the state and behavior of the
 * side navigation, including handling expansion, collapse, and resizing events.
 *
 * Props:
 * - preMainElem: Element to render before the main content.
 * - postMainElem: Element to render after the main content.
 * - bottomElem: Element to render at the bottom of the side navigation.
 * - children: Main content of the side navigation layout.
 * - testId: Optional test identifier for testing purposes.
 *
 */
export function SideNavigationLayout(props: SideNavigationLayoutProps) {
	const { setUserSideNavData } = useUserSideNavData();

	return (
		<AsyncIntlProvider>
			<SideNavVisibilityProvider setUserSideNavData={setUserSideNavData}>
				<SideNavigationLayoutComponent {...props} />
			</SideNavVisibilityProvider>
		</AsyncIntlProvider>
	);
}

function SideNavigationLayoutComponent({
	preMainElem,
	postMainElem,
	bottomElem,
	children,
	testId,
}: SideNavigationLayoutProps) {
	const intl = useIntl();
	const { userSideNavData } = useUserSideNavData();
	const { onCollapse, onExpand, onResizeStart, onResizeEnd } = useSideNavVisibility();
	const { l1DragAndDropEnabled } = useOptions();
	const sideNavContentRef = React.useRef<HTMLDivElement>(null);
	const scrollDirection = useScrollDirection(sideNavContentRef);
	const navAnalytics = useNavAnalytics();

	useEffect(() => {
		if (scrollDirection) {
			navAnalytics.fireScrollSideNavEvent(scrollDirection);
		}
	}, [navAnalytics, scrollDirection]);

	useEffect(() => {
		if (!l1DragAndDropEnabled) {
			return;
		}

		const scrollable = sideNavContentRef.current;
		invariant(scrollable, 'sideNavContentRef must be set');
		return autoScrollForElements({
			element: scrollable,
			canScroll: ({ source }) => source.data.type === 'menu-item',
		});
	}, [l1DragAndDropEnabled]);

	return (
		<SideNav
			testId={testId}
			onExpand={onExpand}
			onCollapse={onCollapse}
			defaultWidth={userSideNavData.defaultWidth}
			defaultCollapsed={userSideNavData.defaultCollapsed}
		>
			{preMainElem && <SideNavHeader>{preMainElem}</SideNavHeader>}
			<SideNavContent ref={sideNavContentRef}>
				{children}
				<TopLevelSpacer />
				{postMainElem}
			</SideNavContent>
			<PanelSplitter
				onResizeEnd={onResizeEnd}
				onResizeStart={onResizeStart}
				label={intl.formatMessage(messages.panelSplitterLabel)}
			/>
			{bottomElem && <SideNavFooter>{bottomElem}</SideNavFooter>}
		</SideNav>
	);
}
